/**
 * Remembers which seller a signed-out shopper was about to buy from.
 *
 * The public seller page (/s/<slug>) is reachable by anyone — that is the
 * whole point of a link shared on Facebook. But buying needs an account, so
 * pressing Buy Now sends a stranger to sign-in. Without this note the app
 * drops them on their dashboard afterwards and the seller is lost, which is
 * exactly the bug the seller page exists to fix.
 *
 * sessionStorage, not localStorage: the note must die with the tab. A stale
 * seller resurfacing on next week's login would hijack a normal sign-in.
 */
const KEY = 'smartfi.sellerReturn';

export function rememberSeller(slug: string): void {
   try {
      if (slug) sessionStorage.setItem(KEY, slug);
   } catch {
      // Private-mode Safari throws on write. Losing the note only costs the
      // shopper a back-tap, so it must never break the sign-in journey.
   }
}

/** Reads the pending seller and clears it — one redirect per note. */
export function takeSeller(): string | null {
   try {
      const slug = sessionStorage.getItem(KEY);
      if (slug) sessionStorage.removeItem(KEY);
      return slug;
   } catch {
      return null;
   }
}

/**
 * The seller also travels in the sign-in URL as ?next=/s/<slug>, so a shopper
 * who reloads the sign-in screen still gets back to the right seller — a
 * reload wipes the in-memory router state and, on a fresh tab, the note above
 * was never written at all.
 *
 * ONLY a seller path is honoured. `next` arrives from the address bar, so
 * accepting anything else would turn sign-in into an open redirect that any
 * link could aim at another site.
 */
const SELLER_PATH = /^\/s\/[A-Za-z0-9._~-]{1,120}$/;

export function readSellerNextPath(): string | null {
   try {
      const next = new URLSearchParams(window.location.search).get('next');
      return next && SELLER_PATH.test(next) ? next : null;
   } catch {
      return null;
   }
}

/** Builds the sign-in URL that carries the seller through the detour. */
export function signInPathForSeller(slug: string): string {
   return `/?next=${encodeURIComponent(`/s/${encodeURIComponent(slug)}`)}`;
}

/**
 * Where to land someone the moment they finish signing in. Checks the URL
 * first, then the stored note, then whatever the caller already intended.
 * Consumes the note either way so it can't fire again later.
 */
export function resolvePostLoginPath(stateFrom?: string): string {
   const fromUrl = readSellerNextPath();
   if (fromUrl) {
      takeSeller();
      return fromUrl;
   }
   const slug = takeSeller();
   if (slug) return `/s/${encodeURIComponent(slug)}`;
   return stateFrom || '/home';
}
